import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sun, ArrowRight, Menu, X, ChevronDown } from "lucide-react";

const PROGRAMMES = [
  { href: "/nlv-approved", label: "NLV Approved", desc: "Spain's Non-Lucrative Visa route" },
  { href: "/eu-approved", label: "EU Approved", desc: "Moving with an EU, EEA or Swiss passport" },
];

const LINKS = [
  { href: "/tools", label: "Free Tools" },
  { href: "/about", label: "About Carley" },
  { href: "/client-success", label: "Client Success" },
];

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isLighter = ["/masterclass", "/tools", "/quiz", "/eu-readiness", "/checklist", "/calculator", "/about", "/client-success"].includes(location.pathname);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isLighter ? "bg-[var(--cream)] shadow-[0_2px_24px_-12px_rgba(0,0,0,0.18)] border-b border-black/5" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="w-9 h-9 rounded-full bg-[var(--terracotta)] flex items-center justify-center text-[var(--cream)]">
            <Sun className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <span className="font-serif text-[1.35rem] tracking-tight text-[var(--navy)] leading-none">
            Get to Spain
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          <div
            className="relative"
            onMouseEnter={() => setProgOpen(true)}
            onMouseLeave={() => setProgOpen(false)}
          >
            <button
              className={`inline-flex items-center gap-1 text-[0.92rem] transition-colors ${
                PROGRAMMES.some((p) => p.href === location.pathname)
                  ? "text-[var(--terracotta)] font-medium"
                  : "text-[var(--ink-soft)] hover:text-[var(--terracotta)]"
              }`}
              onClick={() => setProgOpen(!progOpen)}
              aria-expanded={progOpen}
              data-testid="nav-programmes-toggle"
            >
              Programmes <ChevronDown className={`w-4 h-4 transition-transform ${progOpen ? "rotate-180" : ""}`} />
            </button>
            {progOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-72" data-testid="nav-programmes-menu">
                <div className="rounded-2xl bg-white shadow-xl border border-black/5 p-2">
                  {PROGRAMMES.map((p) => (
                    <a
                      key={p.href}
                      href={p.href}
                      className="block px-4 py-3 rounded-xl hover:bg-[var(--cream-deep)] transition-colors"
                      data-testid={`nav-link-${p.label.toLowerCase().replace(/ /g, "-")}`}
                    >
                      <span className="block font-medium text-[var(--navy)]">{p.label}</span>
                      <span className="block text-xs text-[var(--muted)] mt-0.5">{p.desc}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[0.92rem] text-[var(--ink-soft)] hover:text-[var(--terracotta)] transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#programmes"
            className="hidden sm:inline-flex pill px-5 py-2.5 rounded-full bg-[var(--navy)] text-[var(--cream)] text-sm font-medium items-center gap-2"
            data-testid="nav-cta-find-route"
          >
            Find my route <ArrowRight className="w-4 h-4" />
          </a>
          <button
            className="md:hidden text-[var(--navy)]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[var(--cream)] border-t border-black/5 px-6 py-4 flex flex-col gap-1">
          <a href="/" onClick={() => setOpen(false)} className="py-2 text-[var(--ink)]" data-testid="nav-mobile-home">
            Home
          </a>
          <div className="py-2 text-xs uppercase tracking-[0.25em] text-[var(--muted)]">Programmes</div>
          {PROGRAMMES.map((p) => (
            <a
              key={p.href}
              href={p.href}
              onClick={() => setOpen(false)}
              className="py-2 pl-4 text-[var(--ink)] border-l-2 border-[var(--terracotta)]/40"
              data-testid={`nav-mobile-${p.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {p.label}
            </a>
          ))}
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="py-2 text-[var(--ink)]"
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="/#programmes"
            onClick={() => setOpen(false)}
            className="mt-2 py-3 text-center rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
            data-testid="nav-mobile-find-route"
          >
            Find my route
          </a>
        </div>
      )}
    </header>
  );
}
